import React from 'react';
import WindowType from '../../controls/frame/WindowType';
import WindowShape from '../../controls/frame/WindowShape';
import WindowWidth from '../../controls/frame/WinWidth';
import WinHinges from '../../controls/frame/WinHinges';
import WinSliderOprion from '../../controls/frame/WinSliderOption';
import WinTiltAndTurnOption from '../../controls/frame/WinTiltAndTurnOption';
import { formatToDecimal } from '../../../controls/conversions';
import { minmaxWindow } from '../../../lib/minmax';
import { USE_SHAPES, SHAPES_VALIDATION_ACTIVE } from '../../../constants';

// Helper function to check if a shape value ends with "Left" or "Right"
const endsWithLeft = (value) => value?.endsWith(' Left') || value?.endsWith('Left');
const endsWithRight = (value) => value?.endsWith(' Right') || value?.endsWith('Right');

// Helper function to get the base shape name (without Left/Right)
const getBaseShapeName = (value) => {
  if (!value) return null;
  return value.replace(/ Left$/, '').replace(/ Right$/, '').replace(/Left$/, '').replace(/Right$/, '');
};

// Helper function to calculate disabled options for WindowShape
const getDisabledShapeOptions = (position, mainRowConfValue, shapeValues, windowTypeValues) => {
  const disabledOptions = [];
  if (SHAPES_VALIDATION_ACTIVE === false) {
    return disabledOptions;
  }

  // Only apply rules if there's more than 1 shape in the row
  if (mainRowConfValue < 2) {
    return disabledOptions;
  }

  // Get all shape values BEFORE the current position, but ONLY consider shapes that are visible (WindowType is "Picture")
  const shapesBefore = [];
  for (let i = 0; i < position - 1; i++) {
    if (windowTypeValues[i] === 'Picture') {
      shapesBefore.push(shapeValues[i]);
    } else {
      shapesBefore.push(null); // Keep the position but mark as not visible
    }
  }

  // Also get ALL shape values (to check if a Right exists anywhere for position 1)
  const allShapes = [];
  for (let i = 0; i < mainRowConfValue; i++) {
    if (windowTypeValues[i] === 'Picture') {
      allShapes.push(shapeValues[i]);
    } else {
      allShapes.push(null);
    }
  }

  // Find if we have a "Left" or "Right" shape BEFORE current position
  const leftShapeBefore = shapesBefore.find(endsWithLeft);
  const rightShapeBefore = shapesBefore.find(endsWithRight);
  const hasLeftBefore = !!leftShapeBefore;
  const hasRightBefore = !!rightShapeBefore;

  // Find if we have a "Right" shape ANYWHERE (for position 1 validation)
  const rightShapeAnywhere = allShapes.find(endsWithRight);
  const hasRightAnywhere = !!rightShapeAnywhere;

  // Find position of "Right" shape (1-indexed)
  const rightPosition = shapesBefore.findIndex(endsWithRight) + 1;

  // NEW RULE: Left & Right work like simple parentheses (no nesting, must match)
  // If a Left shape exists BEFORE, disable all Right shapes except the matching one
  if (hasLeftBefore) {
    const baseShapeName = getBaseShapeName(leftShapeBefore);
    const allRightShapes = ['Triangle Right', 'Trapezoid Right', 'AngledTopTrapezoid Right', 'HalfEyebrow Right', 'Eyebrow Right'];
    const matchingRightShape = `${baseShapeName} Right`;

    // Disable all right shapes except the matching one
    allRightShapes.forEach(shape => {
      if (shape !== matchingRightShape) {
        disabledOptions.push(shape);
      }
    });
  }

  // If a Right shape exists ANYWHERE, disable all Left shapes except the matching one
  // This handles position 1 when Right is at position 2+
  if (hasRightAnywhere && position === 1) {
    const baseShapeName = getBaseShapeName(rightShapeAnywhere);
    const allLeftShapes = ['Triangle Left', 'Trapezoid Left', 'AngledTopTrapezoid Left', 'HalfEyebrow Left', 'Eyebrow Left'];
    const matchingLeftShape = `${baseShapeName} Left`;

    // Disable all left shapes except the matching one
    allLeftShapes.forEach(shape => {
      if (shape !== matchingLeftShape) {
        disabledOptions.push(shape);
      }
    });
  }

  // Rule: Only one "Left" allowed in the row (must be at position 1)
  // Rule: "Right" shapes can only appear once, and need a matching "Left" at position 1
  if (position === 1) {
    // Position 1: "Left" shapes allowed, but "Right" shapes NOT allowed (no matching "Left" before them)
    disabledOptions.push('Triangle Right', 'Trapezoid Right', 'AngledTopTrapezoid Right', 'HalfEyebrow Right', 'Eyebrow Right');
  } else {
    // Position 2+: disable "Left" options (they can only be at position 1)
    disabledOptions.push('Triangle Left', 'Trapezoid Left', 'AngledTopTrapezoid Left', 'HalfEyebrow Left', 'Eyebrow Left');

    // If we already have a "Right" shape before current position, disable all "Right" options (only one "Right" allowed)
    if (hasRightBefore) {
      disabledOptions.push('Triangle Right', 'Trapezoid Right', 'AngledTopTrapezoid Right', 'HalfEyebrow Right', 'Eyebrow Right');
    }

    // No shapes allowed after "Right" position
    if (rightPosition > 0 && position > rightPosition) {
      // Disable all options (this position shouldn't exist)
      disabledOptions.push('Rectangle', 'Triangle Left', 'Triangle Right', 'Triangle Center',
        'Trapezoid Left', 'Trapezoid Right', 'AngledTopTrapezoid Left', 'AngledTopTrapezoid Right',
        'Pentagon', 'Octagon', 'HalfEyebrow Left', 'HalfEyebrow Right', 'HalfEyebrow Center',
        'Eyebrow Center', 'Eyebrow Left', 'Eyebrow Right', 'Oculus');
    }
  }

  return disabledOptions;
};

export const getSizingAndFrameSection = (props) => {
  const {
    isDisabled,
    mainRowConfValue,
    setMainRowConfValue,
    windowTypeValue1, setWindowTypeValue1,
    windowTypeValue2, setWindowTypeValue2,
    windowTypeValue3, setWindowTypeValue3,
    windowTypeValue4, setWindowTypeValue4,
    windowTypeValue5, setWindowTypeValue5,

    windowShapeValue1, setWindowShapeValue1,
    windowShapeValue2, setWindowShapeValue2,
    windowShapeValue3, setWindowShapeValue3,
    windowShapeValue4, setWindowShapeValue4,
    windowShapeValue5, setWindowShapeValue5,

    trapezoidLeftSizeValue1, setTrapezoidLeftSizeValue1,
    trapezoidLeftSizeValue2, setTrapezoidLeftSizeValue2,
    trapezoidLeftSizeValue3, setTrapezoidLeftSizeValue3,
    trapezoidLeftSizeValue4, setTrapezoidLeftSizeValue4,
    trapezoidLeftSizeValue5, setTrapezoidLeftSizeValue5,

    trapezoidRightSizeValue1, setTrapezoidRightSizeValue1,
    trapezoidRightSizeValue2, setTrapezoidRightSizeValue2,
    trapezoidRightSizeValue3, setTrapezoidRightSizeValue3,
    trapezoidRightSizeValue4, setTrapezoidRightSizeValue4,
    trapezoidRightSizeValue5, setTrapezoidRightSizeValue5,   

    angledTopTrapezoidLeftSizeValue1, setAngledTopTrapezoidLeftSizeValue1,
    angledTopTrapezoidLeftSizeValue2, setAngledTopTrapezoidLeftSizeValue2,
    angledTopTrapezoidLeftSizeValue3, setAngledTopTrapezoidLeftSizeValue3,
    angledTopTrapezoidLeftSizeValue4, setAngledTopTrapezoidLeftSizeValue4,
    angledTopTrapezoidLeftSizeValue5, setAngledTopTrapezoidLeftSizeValue5,

    angledTopTrapezoidRightSizeValue1, setAngledTopTrapezoidRightSizeValue1,
    angledTopTrapezoidRightSizeValue2, setAngledTopTrapezoidRightSizeValue2,
    angledTopTrapezoidRightSizeValue3, setAngledTopTrapezoidRightSizeValue3,
    angledTopTrapezoidRightSizeValue4, setAngledTopTrapezoidRightSizeValue4,
    angledTopTrapezoidRightSizeValue5, setAngledTopTrapezoidRightSizeValue5,

    windowWidthValue1, setWindowWidthValue1,
    windowWidthValue2, setWindowWidthValue2,
    windowWidthValue3, setWindowWidthValue3,
    windowWidthValue4, setWindowWidthValue4,
    windowWidthValue5, setWindowWidthValue5,

    windowHeightValue,

    hingesValue1, setHingesValue1,
    hingesValue2, setHingesValue2,
    hingesValue3, setHingesValue3,
    hingesValue4, setHingesValue4,
    hingesValue5, setHingesValue5,

    sliderValue1, setSliderValue1,
    sliderValue2, setSliderValue2,
    sliderValue3, setSliderValue3,
    sliderValue4, setSliderValue4,
    sliderValue5, setSliderValue5,

    tiltAndTurnValue1, setTiltAndTurnValue1,
    tiltAndTurnValue2, setTiltAndTurnValue2,
    tiltAndTurnValue3, setTiltAndTurnValue3,
    tiltAndTurnValue4, setTiltAndTurnValue4,
    tiltAndTurnValue5, setTiltAndTurnValue5,

    radiusxValue1, setRadiusxValue1,
    radiusxValue2, setRadiusxValue2,
    radiusxValue3, setRadiusxValue3,
    radiusxValue4, setRadiusxValue4,
    radiusxValue5, setRadiusxValue5,

    radiusyValue1, setRadiusyValue1,
    radiusyValue2, setRadiusyValue2,
    radiusyValue3, setRadiusyValue3,
    radiusyValue4, setRadiusyValue4,
    radiusyValue5, setRadiusyValue5,


    glazingTypeValue1,
    minmaxW,
    setMinMaxW,
  } = props;

  // Collect all shape values and window type values for validation
  const shapeValues = [
    windowShapeValue1,
    windowShapeValue2,
    windowShapeValue3,
    windowShapeValue4,
    windowShapeValue5
  ];

  const windowTypeValues = [
    windowTypeValue1,
    windowTypeValue2,
    windowTypeValue3,
    windowTypeValue4,
    windowTypeValue5
  ];

  return [
    // SizingAndFrame starts...
    { id: "WindowType1",    component: 1 > mainRowConfValue ? null : ( <WindowType
        disabled={isDisabled} id = {"WindowType1"} label={"1. Type"} showPics={true}
        value={windowTypeValue1}    onChange={(newValue) => { setWindowTypeValue1(newValue);
                                                              const modifiedValue = 'End-Vent'? 'END-VENT ' + sliderValue1 : newValue;
                                                              /*setMinMaxW(minmaxWindow(modifiedValue, glazingTypeValue1));*/ }
    }/>), position: [1, 1], status: "active", expandableSectionId: "SizingAndFrame" },
    { id: "WindowType2",    component: 2 > mainRowConfValue ? null : ( <WindowType
        disabled={isDisabled} id = {"WindowType2"} label={"2. Type"} showPics={true}
        value={windowTypeValue2} onChange={(newValue) => { setWindowTypeValue2(newValue); }
    }/>), position: [3, 1], status: "active", expandableSectionId: "SizingAndFrame" },
    { id: "WindowType3",    component: 3 > mainRowConfValue ? null : ( <WindowType
        disabled={isDisabled} id = {"WindowType3"} label={"3. Type"} showPics={true}
        value={windowTypeValue3}  onChange={(newValue) => { setWindowTypeValue3(newValue); }
    }/>), position: [5, 1], status: "active", expandableSectionId: "SizingAndFrame" },
    { id: "WindowType4",    component: 4 > mainRowConfValue ? null : ( <WindowType
        disabled={isDisabled} id = {"WindowType4"} label={"4. Type"} showPics={true}
        value={windowTypeValue4}  onChange={(newValue) => { setWindowTypeValue4(newValue); }
    }/>), position: [7, 1], status: "active", expandableSectionId: "SizingAndFrame" },
    { id: "WindowType5",    component: 5 > mainRowConfValue ? null : ( <WindowType
        disabled={isDisabled} id = {"WindowType5"} label={"5. Type"} showPics={true}
        value={windowTypeValue5} onChange={(newValue) => { setWindowTypeValue5(newValue); }
    }/>), position: [9, 1], status: "active", expandableSectionId: "SizingAndFrame" },


    // WindowShape controls will go here in column 2 (only visible when WindowType is "Picture")
    USE_SHAPES &&
    windowTypeValue1 === 'Picture' && { id: "WindowShape1",    component: 1 > mainRowConfValue ? null : ( <WindowShape
        disabled={isDisabled} id = {"WindowShape1"} label={"1. Shape"} showPics={true}
        value={windowShapeValue1} onChange={(newValue) => { setWindowShapeValue1(newValue); }}
        disabledOptions={getDisabledShapeOptions(1, mainRowConfValue, shapeValues, windowTypeValues)}
    />), position: [1, 2], status: "active", expandableSectionId: "SizingAndFrame" },

    USE_SHAPES &&
    windowTypeValue2 === 'Picture' && { id: "WindowShape2",    component: 2 > mainRowConfValue ? null : ( <WindowShape
        disabled={isDisabled} id = {"WindowShape2"} label={"2. Shape"} showPics={true}
        value={windowShapeValue2} onChange={(newValue) => {
          setWindowShapeValue2(newValue);
          if (endsWithRight(newValue)) setMainRowConfValue("2");
        }}
        disabledOptions={getDisabledShapeOptions(2, mainRowConfValue, shapeValues, windowTypeValues)}
    />), position: [3, 2], status: "active", expandableSectionId: "SizingAndFrame" },

    USE_SHAPES &&
    windowTypeValue3 === 'Picture' && { id: "WindowShape3",    component: 3 > mainRowConfValue ? null : ( <WindowShape
        disabled={isDisabled} id = {"WindowShape3"} label={"3. Shape"} showPics={true}
        value={windowShapeValue3} onChange={(newValue) => {
          setWindowShapeValue3(newValue);
          if (endsWithRight(newValue)) setMainRowConfValue("3");
        }}
        disabledOptions={getDisabledShapeOptions(3, mainRowConfValue, shapeValues, windowTypeValues)}
    />), position: [5, 2], status: "active", expandableSectionId: "SizingAndFrame" },

    USE_SHAPES &&
    windowTypeValue4 === 'Picture' && { id: "WindowShape4",    component: 4 > mainRowConfValue ? null : ( <WindowShape
        disabled={isDisabled} id = {"WindowShape4"} label={"4. Shape"} showPics={true}
        value={windowShapeValue4} onChange={(newValue) => {
          setWindowShapeValue4(newValue);
          if (endsWithRight(newValue)) setMainRowConfValue("4");
        }}
        disabledOptions={getDisabledShapeOptions(4, mainRowConfValue, shapeValues, windowTypeValues)}
    />), position: [7, 2], status: "active", expandableSectionId: "SizingAndFrame" },

    USE_SHAPES &&
    windowTypeValue5 === 'Picture' && { id: "WindowShape5",    component: 5 > mainRowConfValue ? null : ( <WindowShape
        disabled={isDisabled} id = {"WindowShape5"} label={"5. Shape"} showPics={true}
        value={windowShapeValue5} onChange={(newValue) => {
          setWindowShapeValue5(newValue);
          if (endsWithRight(newValue)) setMainRowConfValue("5");
        }}
        disabledOptions={getDisabledShapeOptions(5, mainRowConfValue, shapeValues, windowTypeValues)}
    />), position: [9, 2], status: "active", expandableSectionId: "SizingAndFrame" },

    // WindowWidth
    { id: "WindowWidth1",    component: 1 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"WindowWidth1"} label={"Width"} /*minW={minmaxW?.minW} maxW={minmaxW?.maxH}*/
        value={windowWidthValue1}    onChange={(newValue) => { setWindowWidthValue1(newValue); }
    }/>), position: [1, 3], status: "active", expandableSectionId: "SizingAndFrame" },
    { id: "WindowWidth2",    component: 2 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"WindowWidth2"} label={"Width"}
        value={windowWidthValue2}    onChange={(newValue) => { setWindowWidthValue2(newValue); }
    }/>), position: [3, 3], status: "active", expandableSectionId: "SizingAndFrame" },
    { id: "WindowWidth3",    component: 3 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"WindowWidth3"} label={"Width"}
        value={windowWidthValue3}    onChange={(newValue) => { setWindowWidthValue3(newValue); }
    }/>), position: [5, 3], status: "active", expandableSectionId: "SizingAndFrame" },
    { id: "WindowWidth4",    component: 4 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"WindowWidth4"} label={"Width"}
        value={windowWidthValue4}    onChange={(newValue) => { setWindowWidthValue4(newValue); }
    }/>), position: [7, 3], status: "active", expandableSectionId: "SizingAndFrame" },
    { id: "WindowWidth5",    component: 5 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"WindowWidth5"} label={"Width"}
        value={windowWidthValue5}    onChange={(newValue) => { setWindowWidthValue5(newValue); }
    }/>), position: [9, 3], status: "active", expandableSectionId: "SizingAndFrame" },


    ['Casement', 'Casement2'].includes(windowTypeValue1) && { id: "WinHinges1",
      component: 1 > mainRowConfValue ? null : (
        <WinHinges disabled={isDisabled} id="WinHinges1" label="Hinges" value={hingesValue1}
          onChange={(newValue) => setHingesValue1(newValue)}
        />
      ), position: [1, 3], status: "active", expandableSectionId: "SizingAndFrame",
    },
    ['Casement', 'Casement2'].includes(windowTypeValue2) && { id: "WinHinges2",
      component: 2 > mainRowConfValue ? null : (
        <WinHinges disabled={isDisabled} id="WinHinges2" label="Hinges" value={hingesValue2}
          onChange={(newValue) => setHingesValue2(newValue)}
        />
      ), position: [3, 3], status: "active", expandableSectionId: "SizingAndFrame",
    },
    ['Casement', 'Casement2'].includes(windowTypeValue3) && { id: "WinHinges3",
      component: 3 > mainRowConfValue ? null : (
        <WinHinges disabled={isDisabled} id="WinHinges3" label="Hinges" value={hingesValue3}
          onChange={(newValue) => setHingesValue3(newValue)}
        />
      ), position: [5, 3], status: "active", expandableSectionId: "SizingAndFrame",
    },
    ['Casement', 'Casement2'].includes(windowTypeValue4) && { id: "WinHinges4",
      component: 4 > mainRowConfValue ? null : (
        <WinHinges disabled={isDisabled} id="WinHinges4" label="Hinges" value={hingesValue4}
          onChange={(newValue) => setHingesValue4(newValue)}
        />
      ), position: [7, 3], status: "active", expandableSectionId: "SizingAndFrame",
    },
    ['Casement', 'Casement2'].includes(windowTypeValue5) && { id: "WinHinges5",
      component: 5 > mainRowConfValue ? null : (
        <WinHinges disabled={isDisabled} id="WinHinges5" label="Hinges" value={hingesValue5}
          onChange={(newValue) => setHingesValue5(newValue)}
        />
      ), position: [9, 3], status: "active", expandableSectionId: "SizingAndFrame",
    },


    ['End-Vent', 'Single Slider Tilt', 'Double Slider Tilt'].includes(windowTypeValue1) && { id: "WinSliderOprion1",
      component: 1 > mainRowConfValue ? null : (
        <WinSliderOprion disabled={isDisabled} id="WinSliderOprion1" label="Slider Option" value={sliderValue1}
          onChange={(newValue) => setSliderValue1(newValue)}
        />
      ), position: [1, 3], status: "active", expandableSectionId: "SizingAndFrame",
    },
    ['End-Vent', 'Single Slider Tilt', 'Double Slider Tilt'].includes(windowTypeValue2) && { id: "WinSliderOprion2",
      component: 2 > mainRowConfValue ? null : (
        <WinSliderOprion disabled={isDisabled} id="WinSliderOprion2" label="Slider Option" value={sliderValue2}
          onChange={(newValue) => setSliderValue2(newValue)}
        />
      ), position: [3, 3], status: "active", expandableSectionId: "SizingAndFrame",
    },
    ['End-Vent', 'Single Slider Tilt', 'Double Slider Tilt'].includes(windowTypeValue3) && { id: "WinSliderOprion3",
      component: 3 > mainRowConfValue ? null : (
        <WinSliderOprion disabled={isDisabled} id="WinSliderOprion3" label="Slider Option" value={sliderValue3}
          onChange={(newValue) => setSliderValue3(newValue)}
        />
      ), position: [5, 3], status: "active", expandableSectionId: "SizingAndFrame",
    },
    ['End-Vent', 'Single Slider Tilt', 'Double Slider Tilt'].includes(windowTypeValue4) && { id: "WinSliderOprion4",
      component: 4 > mainRowConfValue ? null : (
        <WinSliderOprion disabled={isDisabled} id="WinSliderOprion4" label="Slider Option" value={sliderValue4}
          onChange={(newValue) => setSliderValue4(newValue)}
        />
      ), position: [7, 3], status: "active", expandableSectionId: "SizingAndFrame",
    },
    ['End-Vent', 'Single Slider Tilt', 'Double Slider Tilt'].includes(windowTypeValue5) && { id: "WinSliderOprion5",
      component: 5 > mainRowConfValue ? null : (
        <WinSliderOprion disabled={isDisabled} id="WinSliderOprion5" label="Slider Option" value={sliderValue5}
          onChange={(newValue) => setSliderValue5(newValue)}
        />
      ), position: [9, 3], status: "active", expandableSectionId: "SizingAndFrame",
    },

    ['Single Hung Tilt', 'Double Hung Tilt', 'Tilt and Turn'].includes(windowTypeValue1) && { id: "WinTiltAndTurnOption1",
      component: 1 > mainRowConfValue ? null : (
        <WinTiltAndTurnOption disabled={isDisabled} id="WinTiltAndTurnOption1" label="Tilt & Turn Option" value={tiltAndTurnValue1}
          onChange={(newValue) => setTiltAndTurnValue1(newValue)}
        />
      ), position: [1, 3], status: "active", expandableSectionId: "SizingAndFrame",
    },
    ['Single Hung Tilt', 'Double Hung Tilt', 'Tilt and Turn'].includes(windowTypeValue2) && { id: "WinTiltAndTurnOption2",
      component: 2 > mainRowConfValue ? null : (
        <WinTiltAndTurnOption disabled={isDisabled} id="WinTiltAndTurnOption2" label="Tilt & Turn Option" value={tiltAndTurnValue2}
          onChange={(newValue) => setTiltAndTurnValue2(newValue)}
        />
      ), position: [3, 3], status: "active", expandableSectionId: "SizingAndFrame",
    },
    ['Single Hung Tilt', 'Double Hung Tilt', 'Tilt and Turn'].includes(windowTypeValue3) && { id: "WinTiltAndTurnOption3",
      component: 3 > mainRowConfValue ? null : (
        <WinTiltAndTurnOption disabled={isDisabled} id="WinTiltAndTurnOption3" label="Tilt & Turn Option" value={tiltAndTurnValue3}
          onChange={(newValue) => setTiltAndTurnValue3(newValue)}
        />
      ), position: [5, 3], status: "active", expandableSectionId: "SizingAndFrame",
    },
    ['Single Hung Tilt', 'Double Hung Tilt', 'Tilt and Turn'].includes(windowTypeValue4) && { id: "WinTiltAndTurnOption4",
      component: 4 > mainRowConfValue ? null : (
        <WinTiltAndTurnOption disabled={isDisabled} id="WinTiltAndTurnOption4" label="Tilt & Turn Option" value={tiltAndTurnValue4}
          onChange={(newValue) => setTiltAndTurnValue4(newValue)}
        />
      ), position: [7, 3], status: "active", expandableSectionId: "SizingAndFrame",
    },
    ['Single Hung Tilt', 'Double Hung Tilt', 'Tilt and Turn'].includes(windowTypeValue5) && { id: "WinTiltAndTurnOption5",
      component: 5 > mainRowConfValue ? null : (
        <WinTiltAndTurnOption disabled={isDisabled} id="WinTiltAndTurnOption5" label="Tilt & Turn Option" value={tiltAndTurnValue5}
          onChange={(newValue) => setTiltAndTurnValue5(newValue)}
        />
      ), position: [9, 3], status: "active", expandableSectionId: "SizingAndFrame",
    },

    //Radius X & Radius Y for Oculus
    false && ['Oculus'].includes(windowTypeValue1) &&
    { id: "RadiusX1",    component: 1 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"RadiusX1"} label={`Radius X 0.5-${formatToDecimal(windowWidthValue1) / 2 - 8}`}
        minW={0.5} maxW={formatToDecimal(windowWidthValue1) / 2 - 8}
        value={radiusxValue1}    onChange={(newValue) => { setRadiusxValue1(newValue); }
    }/>), position: [2, 1], status: "active", expandableSectionId: "SizingAndFrame" },
    false && ['Oculus'].includes(windowTypeValue1) &&
    { id: "RadiusY1",    component: 1 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"RadiusY1"} label={`Radius Y 0.5-${formatToDecimal(windowHeightValue) / 2 - 8}`}
        minW={0.5} maxW={formatToDecimal(windowHeightValue) / 2 - 8}
        value={radiusyValue1}    onChange={(newValue) => { setRadiusyValue1(newValue); }
    }/>), position: [2, 2], status: "active", expandableSectionId: "SizingAndFrame" },

    false && ['Oculus'].includes(windowTypeValue2) &&
    { id: "RadiusX2",    component: 2 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"RadiusX2"} label={`Radius X 0.5-${formatToDecimal(windowWidthValue2) / 2 - 8}`}
        minW={0.5} maxW={formatToDecimal(windowWidthValue2) / 2 - 8}
        value={radiusxValue2}    onChange={(newValue) => { setRadiusxValue2(newValue); }
    }/>), position: [4, 1], status: "active", expandableSectionId: "SizingAndFrame" },
    false && ['Oculus'].includes(windowTypeValue2) &&
    { id: "RadiusY2",    component: 2 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"RadiusY2"} label={`Radius Y 0.5-${formatToDecimal(windowHeightValue) / 2 - 8}`}
        minW={0.5} maxW={formatToDecimal(windowHeightValue) / 2 - 8}
        value={radiusyValue2}    onChange={(newValue) => { setRadiusyValue2(newValue); }
    }/>), position: [4, 2], status: "active", expandableSectionId: "SizingAndFrame" },

    false && ['Oculus'].includes(windowTypeValue3) &&
    { id: "RadiusX3",    component: 3 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"RadiusX3"} label={`Radius X 0.5-${formatToDecimal(windowWidthValue3) / 2 - 8}`}
        minW={0.5} maxW={formatToDecimal(windowWidthValue3) / 2 - 8}
        value={radiusxValue3}    onChange={(newValue) => { setRadiusxValue3(newValue); }
    }/>), position: [6, 1], status: "active", expandableSectionId: "SizingAndFrame" },
    ['Oculus'].includes(windowTypeValue3) &&
    { id: "RadiusY3",    component: 3 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"RadiusY3"} label={`Radius Y 0.5-${formatToDecimal(windowHeightValue) / 2 - 8}`}
        minW={0.5} maxW={formatToDecimal(windowHeightValue) / 2 - 8}
        value={radiusyValue3}    onChange={(newValue) => { setRadiusyValue3(newValue); }
    }/>), position: [6, 2], status: "active", expandableSectionId: "SizingAndFrame" },

    false && ['Oculus'].includes(windowTypeValue4) &&
    { id: "RadiusX4",    component: 4 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"RadiusX4"} label={`Radius X 0.5-${formatToDecimal(windowWidthValue4) / 2 - 8}`}
        minW={0.5} maxW={formatToDecimal(windowWidthValue4) / 2 - 8}
        value={radiusxValue4}    onChange={(newValue) => { setRadiusxValue4(newValue); }
    }/>), position: [8, 1], status: "active", expandableSectionId: "SizingAndFrame" },
    false && ['Oculus'].includes(windowTypeValue4) &&
    { id: "RadiusY4",    component: 4 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"RadiusY4"} label={`Radius Y 0.5-${formatToDecimal(windowHeightValue) / 2 - 8}`}
        minW={0.5} maxW={formatToDecimal(windowHeightValue) / 2 - 8}
        value={radiusyValue4}    onChange={(newValue) => { setRadiusyValue4(newValue); }
    }/>), position: [8, 2], status: "active", expandableSectionId: "SizingAndFrame" },

    false && ['Oculus'].includes(windowTypeValue5) &&
    { id: "RadiusX5",    component: 5 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"RadiusX5"} label={`Radius X 0.5-${formatToDecimal(windowWidthValue5) / 2 - 8}`}
        minW={0.5} maxW={formatToDecimal(windowWidthValue5) / 2 - 8}
        value={radiusxValue5}    onChange={(newValue) => { setRadiusxValue5(newValue); }
    }/>), position: [10, 1], status: "active", expandableSectionId: "SizingAndFrame" },
    false && ['Oculus'].includes(windowTypeValue5) &&
    { id: "RadiusY5",    component: 5 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"RadiusY5"} label={`Radius Y 0.5-${formatToDecimal(windowHeightValue) / 2 - 8}`}
        minW={0.5} maxW={formatToDecimal(windowHeightValue) / 2 - 8}
        value={radiusyValue5}    onChange={(newValue) => { setRadiusyValue5(newValue); }
    }/>), position: [10, 2], status: "active", expandableSectionId: "SizingAndFrame" },

    //Trapezoid Left & Trapezoid Right
    ['Picture'].includes(windowTypeValue1) && ['Trapezoid Left'].includes(windowShapeValue1) &&
    { id: "TrapezoidLeftSize1",    component: 1 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidLeftSize1"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue1) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue1) - 0.5}
        value={trapezoidLeftSizeValue1}    onChange={(newValue) => { setTrapezoidLeftSizeValue1(newValue); }
    }/>), position: [2, 3], status: "active", expandableSectionId: "SizingAndFrame" },
    ['Picture'].includes(windowTypeValue1) && ['Trapezoid Right'].includes(windowShapeValue1) &&
    { id: "TrapezoidRightSize1",    component: 1 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidRightSize1"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue1) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue1) - 0.5}
        value={trapezoidRightSizeValue1}    onChange={(newValue) => { setTrapezoidRightSizeValue1(newValue); }
    }/>), position: [2, 3], status: "active", expandableSectionId: "SizingAndFrame" },

    ['Picture'].includes(windowTypeValue2) && ['Trapezoid Left'].includes(windowShapeValue2) &&
    { id: "TrapezoidLeftSize2",    component: 2 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidLeftSize2"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue2) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue2) - 0.5}
        value={trapezoidLeftSizeValue2}    onChange={(newValue) => { setTrapezoidLeftSizeValue2(newValue); }
    }/>), position: [4, 3], status: "active", expandableSectionId: "SizingAndFrame" },
    ['Picture'].includes(windowTypeValue2) && ['Trapezoid Right'].includes(windowShapeValue2) &&
    { id: "TrapezoidRightSize2",    component: 2 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidRightSize2"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue2) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue2) - 0.5}
        value={trapezoidRightSizeValue2}    onChange={(newValue) => { setTrapezoidRightSizeValue2(newValue); }
    }/>), position: [4, 3], status: "active", expandableSectionId: "SizingAndFrame" },

    ['Picture'].includes(windowTypeValue3) && ['Trapezoid Left'].includes(windowShapeValue3) &&
    { id: "TrapezoidLeftSize3",    component: 3 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidLeftSize3"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue3) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue3) - 0.5}
        value={trapezoidLeftSizeValue3}    onChange={(newValue) => { setTrapezoidLeftSizeValue3(newValue); }
    }/>), position: [6, 3], status: "active", expandableSectionId: "SizingAndFrame" },
    ['Picture'].includes(windowTypeValue3) && ['Trapezoid Right'].includes(windowShapeValue3) &&
    { id: "TrapezoidRightSize3",    component: 3 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidRightSize3"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue3) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue3) - 0.5}
        value={trapezoidRightSizeValue3}    onChange={(newValue) => { setTrapezoidRightSizeValue3(newValue); }
    }/>), position: [6, 3], status: "active", expandableSectionId: "SizingAndFrame" },

    ['Picture'].includes(windowTypeValue4) && ['Trapezoid Left'].includes(windowShapeValue4) &&
    { id: "TrapezoidLeftSize4",    component: 4 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidLeftSize4"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue4) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue4) - 0.5}
        value={trapezoidLeftSizeValue4}    onChange={(newValue) => { setTrapezoidLeftSizeValue4(newValue); }
    }/>), position: [8, 3], status: "active", expandableSectionId: "SizingAndFrame" },
    ['Picture'].includes(windowTypeValue4) && ['Trapezoid Right'].includes(windowShapeValue4) &&
    { id: "TrapezoidRightSize4",    component: 4 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidRightSize4"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue4) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue4) - 0.5}
        value={trapezoidRightSizeValue4}    onChange={(newValue) => { setTrapezoidRightSizeValue4(newValue); }
    }/>), position: [8, 3], status: "active", expandableSectionId: "SizingAndFrame" },

    ['Picture'].includes(windowTypeValue5) && ['Trapezoid Left'].includes(windowShapeValue5) &&
    { id: "TrapezoidLeftSize5",    component: 5 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidLeftSize5"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue5) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue5) - 0.5}
        value={trapezoidLeftSizeValue5}    onChange={(newValue) => { setTrapezoidLeftSizeValue5(newValue); }
    }/>), position: [10, 3], status: "active", expandableSectionId: "SizingAndFrame" },
    ['Picture'].includes(windowTypeValue5) && ['Trapezoid Right'].includes(windowShapeValue5) &&
    { id: "TrapezoidRightSize5",    component: 5 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"TrapezoidRightSize5"} label={`Top side Width 0.5-${formatToDecimal(windowWidthValue5) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowWidthValue5) - 0.5}
        value={trapezoidRightSizeValue5}    onChange={(newValue) => { setTrapezoidRightSizeValue5(newValue); }
    }/>), position: [10, 3], status: "active", expandableSectionId: "SizingAndFrame" },

    //AngledTopTrapezoid Left & AngledTopTrapezoid Right
    ['Picture'].includes(windowTypeValue1) && ['AngledTopTrapezoid Left'].includes(windowShapeValue1) &&
    { id: "AngledTopTrapezoidLeftSize1",    component: 1 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidLeftSize1"} label={`Left Top Offset 0.5-${formatToDecimal(windowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowHeightValue) - 0.5}
        value={angledTopTrapezoidLeftSizeValue1}    onChange={(newValue) => { setAngledTopTrapezoidLeftSizeValue1(newValue); }
    }/>), position: [2, 3], status: "active", expandableSectionId: "SizingAndFrame" },
    ['Picture'].includes(windowTypeValue1) && ['AngledTopTrapezoid Right'].includes(windowShapeValue1) &&
    { id: "AngledTopTrapezoidRightSize1",    component: 1 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidRightSize1"} label={`Right Top Offset 0.5-${formatToDecimal(windowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowHeightValue) - 0.5}
        value={angledTopTrapezoidRightSizeValue1}    onChange={(newValue) => { setAngledTopTrapezoidRightSizeValue1(newValue); }
    }/>), position: [2, 3], status: "active", expandableSectionId: "SizingAndFrame" },

    ['Picture'].includes(windowTypeValue2) && ['AngledTopTrapezoid Left'].includes(windowShapeValue2) &&
    { id: "AngledTopTrapezoidLeftSize2",    component: 2 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidLeftSize2"} label={`Left Top Offset 0.5-${formatToDecimal(windowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowHeightValue) - 0.5}
        value={angledTopTrapezoidLeftSizeValue2}    onChange={(newValue) => { setAngledTopTrapezoidLeftSizeValue2(newValue); }
    }/>), position: [4, 3], status: "active", expandableSectionId: "SizingAndFrame" },
    ['Picture'].includes(windowTypeValue2) && ['AngledTopTrapezoid Right'].includes(windowShapeValue2) &&
    { id: "AngledTopTrapezoidRightSize2",    component: 2 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidRightSize2"} label={`Right Top Offset 0.5-${formatToDecimal(windowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowHeightValue) - 0.5}
        value={angledTopTrapezoidRightSizeValue2}    onChange={(newValue) => { setAngledTopTrapezoidRightSizeValue2(newValue); }
    }/>), position: [4, 3], status: "active", expandableSectionId: "SizingAndFrame" },

    ['Picture'].includes(windowTypeValue3) && ['AngledTopTrapezoid Left'].includes(windowShapeValue3) &&
    { id: "AngledTopTrapezoidLeftSize3",    component: 3 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidLeftSize3"} label={`Left Top Offset 0.5-${formatToDecimal(windowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowHeightValue) - 0.5}
        value={angledTopTrapezoidLeftSizeValue3}    onChange={(newValue) => { setAngledTopTrapezoidLeftSizeValue3(newValue); }
    }/>), position: [6, 3], status: "active", expandableSectionId: "SizingAndFrame" },
    ['Picture'].includes(windowTypeValue3) && ['AngledTopTrapezoid Right'].includes(windowShapeValue3) &&
    { id: "AngledTopTrapezoidRightSize3",    component: 3 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidRightSize3"} label={`Right Top Offset 0.5-${formatToDecimal(windowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowHeightValue) - 0.5}
        value={angledTopTrapezoidRightSizeValue3}    onChange={(newValue) => { setAngledTopTrapezoidRightSizeValue3(newValue); }
    }/>), position: [6, 3], status: "active", expandableSectionId: "SizingAndFrame" },

    ['Picture'].includes(windowTypeValue4) && ['AngledTopTrapezoid Left'].includes(windowShapeValue4) &&
    { id: "AngledTopTrapezoidLeftSize4",    component: 4 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidLeftSize4"} label={`Left Top Offset 0.5-${formatToDecimal(windowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowHeightValue) - 0.5}
        value={angledTopTrapezoidLeftSizeValue4}    onChange={(newValue) => { setAngledTopTrapezoidLeftSizeValue4(newValue); }
    }/>), position: [8, 3], status: "active", expandableSectionId: "SizingAndFrame" },
    ['Picture'].includes(windowTypeValue4) && ['AngledTopTrapezoid Right'].includes(windowShapeValue4) &&
    { id: "AngledTopTrapezoidRightSize4",    component: 4 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidRightSize4"} label={`Right Top Offset 0.5-${formatToDecimal(windowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowHeightValue) - 0.5}
        value={angledTopTrapezoidRightSizeValue4}    onChange={(newValue) => { setAngledTopTrapezoidRightSizeValue4(newValue); }
    }/>), position: [8, 3], status: "active", expandableSectionId: "SizingAndFrame" },

    ['Picture'].includes(windowTypeValue5) && ['AngledTopTrapezoid Left'].includes(windowShapeValue5) &&
    { id: "AngledTopTrapezoidLeftSize5",    component: 5 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidLeftSize5"} label={`Left Top Offset 0.5-${formatToDecimal(windowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowHeightValue) - 0.5}
        value={angledTopTrapezoidLeftSizeValue5}    onChange={(newValue) => { setAngledTopTrapezoidLeftSizeValue5(newValue); }
    }/>), position: [10, 3], status: "active", expandableSectionId: "SizingAndFrame" },
    ['Picture'].includes(windowTypeValue5) && ['AngledTopTrapezoid Right'].includes(windowShapeValue5) &&
    { id: "AngledTopTrapezoidRightSize5",    component: 5 > mainRowConfValue ? null : ( <WindowWidth
        disabled={isDisabled} id = {"AngledTopTrapezoidRightSize5"} label={`Right Top Offset 0.5-${formatToDecimal(windowHeightValue) - 0.5}`} minW={0.5} maxW={formatToDecimal(windowHeightValue) - 0.5}
        value={angledTopTrapezoidRightSizeValue5}    onChange={(newValue) => { setAngledTopTrapezoidRightSizeValue5(newValue); }
    }/>), position: [10, 3], status: "active", expandableSectionId: "SizingAndFrame" },

    // SizingAndFrame ends...
  ];
};
